import { c as _c } from "react/compiler-runtime";
import type { FC, ReactElement } from 'react';
import dynamic from 'next/dynamic';
import NextError from 'next/error';
import Head from 'next/head';
import Script from 'next/script';
import { ButtonLink } from '@sbt-web/ui';
import { HTTPStatusCode } from '@sbt-web/network/types';
import { AdvScripts, LiraAdvContainer, LiraRunAdvScript } from '@sbt-web/adv';
import { createSkyscraperSizeConstraints } from '@/client/components/Adv/Lira/createSkyscraperSizeConstraints';
import { errorNotFoundConfig } from '@/client/components/Adv/Lira/fallbackConfig/desktopErrorConfig';
import { msiteErrorNotFoundConfig } from '@/client/components/Adv/Lira/fallbackConfig/msiteErrorConfig';
import { LiraContainerIds } from '@/client/components/Adv/Lira/liraContainerIds';
import { SkeletonWithAdv } from '@/client/components/Adv/SkeletonWithAdv';
import { ErrorLayout } from '@/client/components/Scaffolding/ErrorLayout';
import { useAdvToggle } from '@/client/hooks/useAdvToggle';
import classes from './styles.module.scss';
const ComputerFlu = dynamic(() => import('./assets').then(a => a.ComputerFlu), {
  ssr: true
});
const TRexAsset = dynamic(() => import('./assets').then(a => a.TRexAsset), {
  ssr: true
});
interface Props {
  statusCode: HTTPStatusCode;
}
const ErrorPage: FC<Props> = t0 => {
  const $ = _c(23);
  const {
    statusCode
  } = t0;
  let title;
  let message;
  let image;
  const {
    noAdvToggle,
    optimizelyReady
  } = useAdvToggle();
  bb0: switch (statusCode) {
    case HTTPStatusCode.BadRequest:
    case HTTPStatusCode.NotFound:
      {
        title = "Su Subito trovi tutto. Questa pagina, per\xF2, non c'\xE8...";
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <p>Ci dispiace: non siamo riusciti a trovare la pagina che hai richiesto.<br />È solo una pagina, per fortuna, non l'affare dei tuoi sogni.</p>;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        message = t1;
        let t2;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <TRexAsset />;
          $[1] = t2;
        } else {
          t2 = $[1];
        }
        image = t2;
        break bb0;
      }
    case HTTPStatusCode.InternalServerError:
    case HTTPStatusCode.BadGateway:
    case HTTPStatusCode.ServiceUnavailable:
    case HTTPStatusCode.GatewayTimeout:
      {
        title = "Si \xE8 verificato un errore.";
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <br />;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        let t2;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t2 = <p>Ti preghiamo di riprovare tra qualche istante. {t1} Se il problema dovesse persistere, ti preghiamo di<a href="https://assistenza.subito.it/hc/it/"><strong> contattare il servizio clienti</strong></a></p>;
          $[3] = t2;
        } else {
          t2 = $[3];
        }
        message = t2;
        let t3;
        if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
          t3 = <ComputerFlu />;
          $[4] = t3;
        } else {
          t3 = $[4];
        }
        image = t3;
        break bb0;
      }
    default:
      {
        let t1;
        if ($[5] !== statusCode) {
          t1 = <NextError statusCode={statusCode} />;
          $[5] = statusCode;
          $[6] = t1;
        } else {
          t1 = $[6];
        }
        return t1;
      }
  }
  let t1;
  let t2;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      id: LiraContainerIds.pageError.skyscraperRight,
      enabled: true
    };
    t2 = {
      id: LiraContainerIds.pageError.skyscraperLeft,
      enabled: true
    };
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  let t3;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Head><title>Subito.it</title></Head>;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== noAdvToggle || $[11] !== optimizelyReady || $[12] !== statusCode) {
    t4 = statusCode === HTTPStatusCode.NotFound ? <><AdvScripts Script={Script} config={{
        includeOw: false,
        includeTam: false,
        includeAdSense: true,
        includePrebid: true,
        publicPath: "/static/script"
      }} />{optimizelyReady && noAdvToggle !== "B" && <LiraRunAdvScript vertical="subito" pageType="errornotfound" env={process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT} fallbackHebeConfig={{
        desktop: errorNotFoundConfig("subito"),
        msite: msiteErrorNotFoundConfig("subito")
      }} constraintsByContainerId={createSkyscraperSizeConstraints(LiraContainerIds.pageError.skyscraperLeft, LiraContainerIds.pageError.skyscraperRight)} />}</> : null;
    $[10] = noAdvToggle;
    $[11] = optimizelyReady;
    $[12] = statusCode;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== image || $[15] !== message || $[16] !== title) {
    t5 = <ErrorLayout title={title} message={message} image={image} />;
    $[14] = image;
    $[15] = message;
    $[16] = title;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  let t7;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className={classes["btn-container"]}><ButtonLink design="outline" href="/">Torna in homepage</ButtonLink></div>;
    t7 = <div><LiraAdvContainer idForAd={LiraContainerIds.pageError.belowTheFold} className={classes["slot-adv"]} /></div>;
    $[18] = t6;
    $[19] = t7;
  } else {
    t6 = $[18];
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t5) {
    t8 = <SkeletonWithAdv skyscraperRight={t1} skyscraperLeft={t2}>{t3}{t4}{t5}{t6}{t7}</SkeletonWithAdv>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
export default ErrorPage;